import React, { useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, DropdownSingleSelect, Flex } from '@opendoor/bricks/core';

import { OdProtosListingsData_CustomerDeclineReason as CustomerDeclineReason } from '../../__generated__/athenaGlobalTypes';
import { useCustomerDecline } from '../../graphql/athena/mutations/CustomerDecline';

type DidNotConvertFormProps = {
  lwcProductId: string;
};

interface DidNotConvertOption {
  label: string;
  value: CustomerDeclineReason;
}

/** Did not convert options array */
export const DID_NOT_CONVERT_OPTIONS: DidNotConvertOption[] = [
  {
    label: 'Competitor',
    value: CustomerDeclineReason.DECLINE_REASON_COMPETITOR,
  },
  {
    label: 'Non-responsive',
    value: CustomerDeclineReason.DECLINE_REASON_NON_RESPONSIVE,
  },
  {
    label: 'Not buying or selling',
    value: CustomerDeclineReason.DECLINE_REASON_NOT_BUYING_OR_SELLING,
  },
];

/** Sets a reason for the customer not converting */
export const DidNotConvertForm: React.FC<DidNotConvertFormProps> = ({ lwcProductId }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const { customerDecline, error } = useCustomerDecline();

  const handleDidNotConvert = async (reason: CustomerDeclineReason) => {
    customerDecline(lwcProductId, reason)
      .then(() => {
        showToast(
          'info',
          `Successfully set did not convert reason: ${reason}`,
          'lwc-dashboard-toast}',
          {
            toastOptions: { autoClose: 3000 },
          },
        );
      })
      .catch((_) => {
        // Just catch for now, log later
      });
  };

  const handleOptionChange = (option: string) => {
    setSelectedOption(option);
  };
  if (error) {
    showToast('error', 'Error setting did not convert status', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  return (
    <Flex flexDirection="column">
      {showForm ? (
        <>
          <DropdownSingleSelect
            aria-labelledby=""
            autoComplete="off"
            title="Why did the customer not convert?"
            placeholder="Select an option"
            selectedValue={selectedOption ? [selectedOption] : []}
            onChange={(option) => {
              if (typeof option === 'string') {
                handleOptionChange(option);
              }
            }}
            options={DID_NOT_CONVERT_OPTIONS}
          />
          <Button
            my={3}
            aria-label="Submit reason the customer did not convert"
            analyticsName="set-did-not-convert-reason"
            onClick={() => {
              handleDidNotConvert(selectedOption as CustomerDeclineReason);
            }}
            disabled={!selectedOption}
          >
            Submit reason
          </Button>
        </>
      ) : (
        <Button
          aria-label="Show did not convert form"
          analyticsName="show-did-not-convert-form"
          onClick={() => {
            setShowForm(true);
          }}
          variant="secondary"
        >
          Customer did not convert
        </Button>
      )}
    </Flex>
  );
};
